import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Typography, Tag, Popconfirm, Row, Col, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;
const { Option } = Select;

const BranchesPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    const fetchBranches = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/master/branches');
            setBranches(data);
        } catch (error) {
            message.error('Failed to fetch branches');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/master/branches/${id}`);
            message.success('Branch deleted successfully');
            fetchBranches();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to delete branch');
        }
    };

    const onFinish = async (values) => {
        try {
            if (editingId) {
                await client.put(`/master/branches/${editingId}`, values);
                message.success('Branch updated successfully');
            } else {
                await client.post('/master/branches', values);
                message.success('Branch created successfully');
            }
            setIsModalVisible(false);
            fetchBranches();
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Operation failed');
        }
    };

    const columns = [
        {
            title: 'Branch Code',
            dataIndex: 'code',
            key: 'code',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Branch Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            render: (text) => <Typography.Text type="secondary" style={{ fontSize: 13 }}>{text || 'No address provided'}</Typography.Text>
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'} style={{ borderRadius: 12 }}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined style={{ color: '#3b82f6' }} />}
                        onClick={() => handleEdit(record)}
                        className="action-btn"
                    />
                    <Popconfirm
                        title="Delete Branch"
                        description="Are you sure you want to delete this branch?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                            danger
                            className="action-btn"
                        />
                    </Popconfirm>
                </Space>
            ),
        }
    ].filter(col => col.key !== 'actions' || isAdmin);

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Branches</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage company branches and operational hubs.</Typography.Text>
                </div>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={handleAdd}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px' }}
                    >
                        Add Branch
                    </Button>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} md={8}>
                    <StatCard
                        title="Total Branches"
                        value={branches.length}
                        icon={<i className="fa-solid fa-code-branch"></i>}
                        color="#3b82f6"
                        subText="Active and registered"
                    />
                </Col>
                <Col xs={24} md={8}>
                    <StatCard
                        title="Operational"
                        value={(branches || []).filter(b => b.is_active).length}
                        icon={<i className="fa-solid fa-check-double"></i>}
                        color="#10b981"
                        subText="Currently serving"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={branches}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 800 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title={editingId ? 'Edit Branch' : 'Create New Branch'}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={500}
                className="premium-modal"
            >
                <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ is_active: true }}>
                    <Form.Item name="code" label="Branch Code" rules={[{ required: true, message: 'Branch code is required' }]}>
                        <Input placeholder="BR-001" style={{ height: 40, borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item name="name" label="Branch Name" rules={[{ required: true, message: 'Branch name is required' }]}>
                        <Input style={{ height: 40, borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item name="address" label="Physical Address">
                        <Input.TextArea rows={3} style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item name="is_active" label="Operational Status">
                        <Select style={{ height: 40 }}>
                            <Option value={true}>Active</Option>
                            <Option value={false}>Inactive</Option>
                        </Select>
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ height: 40, borderRadius: 8, padding: '0 24px' }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ height: 40, borderRadius: 8, padding: '0 24px', fontWeight: 600 }}>
                            {editingId ? 'Save Changes' : 'Create Branch'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default BranchesPage;




